import type { IsoDate, OccurrenceGuard, Task } from '@hestia/core';

/** What a queued write looks like on disk.
 *
 *  Two rules shape this list. Every op must be **replayable**: sending it twice
 *  has to leave the server in the state sending it once would. And every op must
 *  carry what the device *believed* when it was made, so the server can tell an
 *  edit that is merely late from one that is genuinely stale.
 *
 *  Notably there is no `quickAdd` op. Plain-English text is parsed on the device
 *  the moment it is typed — the user has already seen what it resolved to, and a
 *  `create` with a fixed id replays safely where re-parsing the text would make a
 *  second task. */
export type Op =
  | {
      kind: 'create';
      taskId: string;
      input: {
        id: string; title: string; notes: string | null;
        startDate: IsoDate; startTime: string | null; durationMin: number | null;
        assigneeId: string | null; rrule: string | null; tags: string[];
      };
    }
  | {
      kind: 'update';
      taskId: string;
      patch: Record<string, unknown>;
      /** `updatedAt` of the task as the device last saw it, in epoch ms.
       *  Zero when there is no server version to compare against — a task made
       *  on this device whose create has not drained yet. */
      baseUpdatedAt: number;
    }
  | { kind: 'delete'; taskId: string }
  | {
      kind: 'complete';
      taskId: string;
      /** The day the *schedule* produced, which is what an exception is keyed by. */
      date: IsoDate;
      done: boolean;
      baseStatus: OccurrenceGuard;
    }
  | { kind: 'move'; taskId: string; date: IsoDate; to: IsoDate; baseStatus: OccurrenceGuard };

export interface QueuedOp {
  /** Assigned by IndexedDB. Ops drain in this order, which is the order they were made. */
  seq: number;
  at: number;
  op: Op;
  /** A title to name the thing in a message to the user, without a lookup. */
  label: string;
}

/** A write that came back refused because the world had moved on. Kept only long
 *  enough to tell someone; never retried, because retrying is precisely what the
 *  server said not to do. */
export interface Conflict {
  id: string;
  label: string;
  kind: Op['kind'];
  at: number;
}

export const guardOf = (i: { done: boolean; date: IsoDate; originalDate: IsoDate }): OccurrenceGuard =>
  i.done ? 'done' : i.date !== i.originalDate ? 'moved' : 'none';

/** Epoch ms from the ISO string the API sends, for the update guard. */
export const stampOf = (t: Task): number => Date.parse(t.updatedAt) || 0;
